import { propertyLocation } from '../constants/propertyLocation';

/** IEM archived RIDGE PNG for one volume scan (see mesonet.agron.iastate.edu/GIS/ridge.phtml). */
export function buildIemRidgeFrameUrl(radarId: string, product: string, scanIso: string): string {
  const stamp = isoScanToStamp(scanIso);
  const y = stamp.slice(0, 4);
  const m = stamp.slice(4, 6);
  const d = stamp.slice(6, 8);
  return `https://mesonet.agron.iastate.edu/archive/data/${y}/${m}/${d}/GIS/ridge/${radarId}/${product}/${radarId}_${product}_${stamp}.png`;
}

/** 2026-06-04T19:29Z → 202606041929 */
export function isoScanToStamp(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) throw new Error(`Invalid radar scan time: ${iso}`);
  return `${m[1]}${m[2]}${m[3]}${m[4]}${m[5]}`;
}

export function formatIemRadarRangeUtc(date: Date): string {
  return `${date.toISOString().slice(0, 16)}Z`;
}

export const IEM_RADAR_JSON = 'https://mesonet.agron.iastate.edu/json/radar.py';
/** Single-site super-res base reflectivity (noisy; prefer mosaic for display). */
export const IEM_RADAR_PRODUCT = 'N0B';

/** IEM CONUS base reflectivity — current scan (reliable TMS). */
export const IEM_RADAR_CURRENT_LAYER = 'nexrad-n0q';

export type RadarLoopFrame = {
  layerId: string;
  /** Local time the scan was taken (property timezone). */
  timeLabel: string;
};

const RADAR_TIME_FMT: Intl.DateTimeFormatOptions = {
  timeZone: propertyLocation.timezone,
  weekday: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/** Approximate scan time for a mosaic layer offset (minutes ago from now). */
export function radarFrameTimeLabel(minutesAgo: number, now = new Date()): string {
  const t = new Date(now.getTime() - minutesAgo * 60_000);
  return t.toLocaleString('en-US', RADAR_TIME_FMT);
}

/**
 * CONUS NEXRAD reflectivity (~1h loop, 5‑min steps, oldest first; ends on current).
 * Uses nexrad-n0q (not MRMS lcref) because recent MRMS timestamps often 503.
 */
export function buildRadarReflectivityLoopLayers(now = new Date()): RadarLoopFrame[] {
  const frames: RadarLoopFrame[] = [];
  for (let minutesAgo = 55; minutesAgo >= 5; minutesAgo -= 5) {
    frames.push({
      layerId: `nexrad-n0q-m${String(minutesAgo).padStart(2, '0')}m`,
      timeLabel: radarFrameTimeLabel(minutesAgo, now),
    });
  }
  frames.push({
    layerId: IEM_RADAR_CURRENT_LAYER,
    timeLabel: radarFrameTimeLabel(0, now),
  });
  return frames;
}

/** @deprecated Use buildRadarReflectivityLoopLayers */
export function buildNexradMosaicLoopLayers(): string[] {
  return buildRadarReflectivityLoopLayers().map(f => f.layerId);
}

/** TMS tile for IEM NEXRAD/MRMS mosaic layer (see mesonet.agron.iastate.edu/ogc/). */
export function buildIemMosaicTileUrl(
  layerId: string,
  zoom: number,
  x: number,
  y: number
): string {
  return `https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/${layerId}/${zoom}/${x}/${y}.png`;
}

export function buildIemRidgeLayerId(radarId: string, product: string, scanIso: string): string {
  return `ridge::${radarId}-${product}-${isoScanToStamp(scanIso)}`;
}

/** Spherical mercator tile for RIDGE archive (pairs with OSM basemap). */
export function buildIemRidgeTileUrl(
  radarId: string,
  product: string,
  scanIso: string,
  zoom: number,
  x: number,
  y: number
): string {
  const layer = buildIemRidgeLayerId(radarId, product, scanIso);
  return `https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/${layer}/${zoom}/${x}/${y}.png`;
}
